<template>
  <div class="br-tab-stock-expired">
    <section>
      <KpiCards class="mb-3" :items="kpiItems" :loading="loading" />

      <!-- Main Panel -->
      <v-card class="br-panel mb-3" flat>
        <v-card-text class="pb-2 pt-3">
          <!-- Panel Header -->
          <div class="d-flex align-center flex-wrap br-panel-header">
            <span class="br-accent-bar" />
            <h1 class="br-panel-title">{{ $t("expired_products") || "ສິນຄ້າໝົດອາຍຸ" }}</h1>
            <span class="br-panel-count">{{ formatNumber(total) }} {{ $t("items") || "ລາຍການ" }}</span>
            <v-spacer />
          </div>

          <!-- Filters Row -->
          <div class="d-flex align-center flex-wrap br-filter-row mt-3">
            <TextSearch
              v-model="search"
              class="br-search"
              :placeholder="$t('search') || 'ຄົ້ນຫາ...'"
              @search="onSearch"
              @onClear="clearSearch"
            />

            <div class="d-flex align-center br-date-range">
              <DatePicker
                :dense="true"
                :placeholder="false"
                :label="$t('expired_date') || 'ວັນທີໝົດອາຍຸ'"
                v-model="date"
                hide-details
                style="max-width: 170px"
              />
            </div>

            <span v-if="isFiltered" class="br-clear-link" @click="clearFilters">
              {{ $t("clear_filters") || "ລ້າງຕົວຕອງ" }}
            </span>

            <v-spacer />

            <div class="br-export-btn" @click="exportToCsv">
              <v-icon size="13" class="mr-1">fal fa-file-export</v-icon>
              {{ $t("export") || "ສົ່ງອອກ" }}
            </div>
          </div>

          <!-- Table Wrapper -->
          <div class="br-table-wrap mt-3">
            <div
              v-if="!loading && items.length === 0"
              class="d-flex flex-column align-center justify-center py-12"
            >
              <v-icon size="64" color="grey lighten-1">fal fa-calendar-check</v-icon>
              <div class="mt-4 text-subtitle-1 font-weight-medium grey--text text--darken-2">
                {{ $t("no_expired_products") || "ບໍ່ມີສິນຄ້າໝົດອາຍຸ" }}
              </div>
              <div class="mt-1 grey--text text-body-2">
                {{ $t("all_products_valid") || "ສິນຄ້າທັງໝົດຍັງຢູ່ໃນກຳນົດອາຍຸການໃຊ້ງານ" }}
              </div>
            </div>

            <v-data-table
              v-else
              :headers="headers"
              :items="items"
              item-key="id"
              :loading="loading"
              :loading-text="$t('loading')"
              :no-data-text="$t('no_data')"
              :server-items-length="total"
              :page="page"
              :items-per-page="perPage"
              :footer-props="{
                'items-per-page-options': rowsPerPageItems,
                'items-per-page-text': $t('rows_per_page'),
              }"
              class="br-data-table elevation-0"
              disable-sort
              @update:page="changePage"
              @update:items-per-page="changePerPage"
            >
              <!-- Index Column -->
              <template v-slot:item.no="{ index }">
                <span class="text-caption grey--text">
                  {{ (page - 1) * perPage + index + 1 }}
                </span>
              </template>

              <!-- Product Name Column -->
              <template v-slot:item.product_name="{ item }">
                <div class="d-flex align-center py-1">
                  <v-avatar size="36" rounded class="mr-2 grey lighten-3">
                    <img v-if="item.img" :src="formatImg(item.img)" :alt="item.name" />
                    <v-icon v-else size="18" color="grey">fal fa-box</v-icon>
                  </v-avatar>
                  <div>
                    <div class="font-weight-medium text-body-2">{{ item.name }}</div>
                    <div class="text-caption grey--text">
                      <span v-if="item.code">{{ item.code }}</span>
                      <span v-if="item.barcode" class="ml-1">• {{ item.barcode }}</span>
                    </div>
                  </div>
                </div>
              </template>

              <!-- Category Column -->
              <template v-slot:item.category="{ item }">
                <span>{{ item.category ? (item.category.la_name || item.category.en_name) : "—" }}</span>
              </template>

              <!-- Expired Date Column -->
              <template v-slot:item.expired_date="{ item }">
                <div>
                  <v-chip x-small label color="error" class="font-weight-bold">
                    <v-icon x-small left>fal fa-calendar-times</v-icon>
                    {{ item.expired_date ? formatDate(item.expired_date) : "—" }}
                  </v-chip>
                  <div class="text-caption error--text mt-1">
                    {{ daysExpired(item.expired_date) }}
                  </div>
                </div>
              </template>

              <!-- Stock Column -->
              <template v-slot:item.stock="{ item }">
                <span class="font-weight-bold" :class="item.stock > 0 ? 'error--text' : 'grey--text'">
                  {{ formatNumber(item.stock) }}
                </span>
                <span v-if="item.size" class="text-caption grey--text ml-1">
                  {{ item.size.name }}
                </span>
              </template>

              <!-- Price / Cost Column -->
              <template v-slot:item.cost="{ item }">
                <span>{{ formatNumber(item.cost || item.cost_price || 0) }} ₭</span>
              </template>

              <!-- Total Loss Value Column -->
              <template v-slot:item.total_value="{ item }">
                <span class="font-weight-bold error--text">
                  {{ formatNumber((item.stock || 0) * (item.cost || item.cost_price || 0)) }} ₭
                </span>
              </template>

              <!-- Supplier Column -->
              <template v-slot:item.vendor="{ item }">
                <span class="text-caption grey--text">
                  {{ item.vendor ? item.vendor.name : "—" }}
                </span>
              </template>
            </v-data-table>
          </div>
        </v-card-text>
      </v-card>
    </section>
  </div>
</template>

<script>
import TextSearch from "@/components/global/TextSearch";
import DatePicker from "@/components/global/DatePicker";
import { myMixin } from "@/mixin/main";

const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
  .toISOString()
  .substr(0, 10);

export default {
  name: "TabStockExpired",
  mixins: [myMixin],
  components: {
    TextSearch,
    DatePicker,
  },
  data() {
    return {
      date: today,
      search: "",
      items: [],
      total: 0,
      page: 1,
      perPage: 25,
      loading: false,
    };
  },
  computed: {
    rowsPerPageItems() {
      return this.$store.getters["index/rowsPerPageItems"] || [15, 30, 50];
    },
    kpiItems() {
      return [
        {
          key: "expired",
          label: this.$t("expired_products") || "ສິນຄ້າໝົດອາຍຸ",
          icon: "fal fa-calendar-times",
          iconColor: "error",
          value: this.formatNumber(this.total),
          note: this.$t("items") || "ລາຍການ",
        },
        {
          key: "qty",
          label: this.$t("total_stock") || "ຈຳນວນໃນສະຕັອກ",
          icon: "fal fa-boxes",
          iconColor: "warning",
          value: this.formatNumber(this.totalExpiredQty),
          note: this.$t("units_on_hand") || "ໜ່ວຍຄົງເຫຼືອ",
        },
        {
          key: "cost",
          label: this.$t("total_cost") || "ມູນຄ່າຕົ້ນທຶນ",
          icon: "fal fa-coins",
          value: `${this.formatNumber(this.totalExpiredValue)} ₭`,
          note: this.$t("estimated_loss") || "ມູນຄ່າເສຍຫາຍ",
        },
      ];
    },
    headers() {
      return [
        { text: "#", value: "no", align: "center", width: "50px", sortable: false },
        { text: this.$t("product_name") || "ສິນຄ້າ", value: "product_name", sortable: false },
        { text: this.$t("category") || "ໝວດໝູ່", value: "category", sortable: false },
        { text: this.$t("expired_date") || "ວັນໝົດອາຍຸ", value: "expired_date", sortable: false },
        { text: this.$t("stock") || "ຈຳນວນຄົງເຫຼືອ", value: "stock", align: "end", sortable: false },
        { text: this.$t("cost") || "ຕົ້ນທຶນ/ໜ່ວຍ", value: "cost", align: "end", sortable: false },
        { text: this.$t("total_value") || "ມູນຄ່າເສຍຫາຍ", value: "total_value", align: "end", sortable: false },
        { text: this.$t("supplier") || "ຜູ້ສະໜອງ", value: "vendor", sortable: false },
      ];
    },
    totalExpiredQty() {
      return this.items.reduce((sum, item) => sum + (Number(item.stock) || 0), 0);
    },
    totalExpiredValue() {
      return this.items.reduce(
        (sum, item) => sum + (Number(item.stock) || 0) * (Number(item.cost || item.cost_price) || 0),
        0
      );
    },
    isFiltered() {
      return this.date !== today || !!this.search;
    },
  },
  watch: {
    date() {
      this.fetchItems(1);
    },
  },
  created() {
    this.fetchItems(1);
  },
  methods: {
    formatNumber(val) {
      return Number(val || 0).toLocaleString("en-US");
    },
    formatDate(dateStr) {
      if (!dateStr) return "—";
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString("en-GB");
    },
    formatImg(img) {
      return Help ? Help.img(img) : img;
    },
    daysExpired(dateStr) {
      if (!dateStr) return "";
      const exp = new Date(dateStr);
      const now = new Date(today);
      const diffTime = now.getTime() - exp.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays <= 0) return this.$t("expires_today") || "ໝົດອາຍຸມື້ນີ້";
      return `${this.$t("expired") || "ໝົດອາຍຸ"} ${diffDays} ${this.$t("days_ago") || "ມື້ກ່ອນ"}`;
    },
    async fetchItems(page = 1) {
      this.page = page;
      this.loading = true;
      try {
        let url = `product_expire?date=${this.date}&page=${this.page}&per_page=${this.perPage}`;
        if (this.search && this.search.trim()) {
          url += `&q=${encodeURIComponent(this.search.trim())}`;
        }
        const { data } = await this.$http.get(url);
        if (data.status && data.data) {
          this.items = data.data.data || [];
          this.total = data.data.total || 0;
        } else {
          this.items = [];
          this.total = 0;
        }
      } catch (err) {
        console.error("Error fetching expired products:", err);
        this.items = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },
    onSearch() {
      this.fetchItems(1);
    },
    clearSearch() {
      this.search = "";
      this.fetchItems(1);
    },
    clearFilters() {
      this.search = "";
      this.date = today;
      this.fetchItems(1);
    },
    changePage(p) {
      this.fetchItems(p);
    },
    changePerPage(pp) {
      this.perPage = pp;
      this.fetchItems(1);
    },
    exportToCsv() {
      if (!this.items.length) return;
      const headers = ["Name", "Code", "Category", "Expired Date", "Stock", "Cost", "Total Value", "Supplier"];
      const rows = this.items.map((i) => [
        `"${i.name || ""}"`,
        `"${i.code || ""}"`,
        `"${i.category ? i.category.name_la || i.category.en_name : ""}"`,
        `"${i.expired_date || ""}"`,
        i.stock || 0,
        i.cost || i.cost_price || 0,
        (i.stock || 0) * (i.cost || i.cost_price || 0),
        `"${i.vendor ? i.vendor.name : ""}"`,
      ]);
      const csvContent = "data:text/csv;charset=utf-8,\uFEFF" + [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `expired_products_${this.date}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  },
};
</script>

<style scoped>
.br-tab-stock-expired {
  width: 100%;
}
</style>
